import {Button} from "@zendeskgarden/react-buttons";
import {Field, Input, Label, Textarea} from "@zendeskgarden/react-forms";
import {Body, Close, Footer, FooterItem, Header, Modal} from "@zendeskgarden/react-modals";
import {Tag} from "@zendeskgarden/react-tags";
import {ReactElement, useContext, useEffect, useMemo, useState} from "react";
import {VersionType} from "../../__generated__/generated_types";
import {userIsAdmin} from "../../authorization/AuthorizationCheckers";
import {AuthContext} from "../../contexts/AuthContext";
import BarLoader from "../bar-loader/BarLoader";
import {DriverNote, DriverNoteLineType} from "../driver-note/DriverNoteModal";
import useAccountNotes from "./logic/useAccountNotes";
import * as css from "./styles/account-note-modal.module.scss";


export interface AccountNote {
    accountId: string;
    propertyId: string | undefined;
    budgetYear: number;
    versionType: VersionType;
    text: string | null;
}

export interface AccountNoteModalFetchInputs {
    propertyId: string;
    accountId: string;
    budgetYear: number;
    versionType: VersionType;
}

export interface AccountNoteModalProps {
    accountName: string;
    noteFetchInputs: AccountNoteModalFetchInputs | null;
    onSave?: (accountNote: AccountNote) => void;
    onClose?: () => void;
}

export default function AccountNoteModal({accountName, noteFetchInputs, onSave, onClose}: AccountNoteModalProps): ReactElement {
    const {user} = useContext(AuthContext);

    const {
        accountNote,
        driverNotes,
        isBusy,
        fetchNotes,
        saveNote,
    } = useAccountNotes({onSave});

    const [localNote, setLocalNote] = useState<string>("");
    const [filterText, setFilterText] = useState<string>("");

    useEffect(() => {
        if (noteFetchInputs) {
            const {
                propertyId,
                accountId,
                budgetYear,
                versionType,
            } = noteFetchInputs;
            fetchNotes({propertyId, accountId, budgetYear, versionType});
        }
    }, [noteFetchInputs]);

    useEffect(() => {
        setLocalNote(accountNote?.text ?? "");
    }, [accountNote?.text]);

    const filteredDriverList = useMemo<DriverNote[]>(() => {
        const trimmedFilter = filterText.toLowerCase().trim();
        if (filterText === "") {
            return driverNotes;
        }
        return driverNotes.filter(note => note.lineName.toLowerCase().includes(trimmedFilter) || note.note?.toLowerCase().includes(trimmedFilter));
    }, [driverNotes, filterText]);

    // apparently we let people edit notes even if the account or forecast period is locked?
    // leaving it like this to maintain current behavior, but if that's wrong then locked check gets added and goes here
    const userCanEdit = useMemo<boolean>(() => {
        if (!user) {
            return false;
        }

        if (userIsAdmin(user)) {
            return true;
        }

        return true;
    }, [user]);

    const saveButtonLabel = useMemo<string>(() => {
        if (isBusy) {
            return "Working...";
        }
        if (accountNote?.text === localNote.trim()) {
            return "No Changes";
        }
        if (accountNote?.text === "") {
            return "Add Account Note";
        }
        return "Update Account Note"
    }, [isBusy, accountNote?.text, localNote]);

    function getDriverStyleClass(driverType: DriverNoteLineType): string {
        switch (driverType) {
            case "worksheet":
                return css.worksheetDriver;
            case "operational":
                return css.operationalDriver;
            case "custom":
                return css.customDriver;
            default:
                return "";
        }
    }

    function getDriverLabel(driverType: DriverNoteLineType): string {
        switch (driverType) {
            case "worksheet":
                return "Worksheet Line Item";
            case "operational":
                return "Operational Driver";
            case "custom":
                return "Custom Driver";
            default:
                return "";
        }
    }

    if (!noteFetchInputs) {
        return <></>;
    }

    return (
        <Modal onClose={onClose} className={css.accountNoteModal}>
            <Header>Notes for Account "{accountName}"</Header>
            <Body className={css.modalBody}>
                <BarLoader loading={isBusy} />
                <Field>
                    <Label>Account Note</Label>
                    <Textarea
                        placeholder="Write anything you would like people to know, regarding this account."
                        disabled={isBusy}
                        minRows={3}
                        maxRows={12}
                        value={localNote}
                        onChange={evt => setLocalNote(evt.target.value)}
                    />
                </Field>
                {driverNotes.length > 0 &&
                    <>
                        <div className={css.driverNotesHeader}>
                            <h4 className={css.driverNotesHeaderLabel}>Driver Notes</h4>
                            <Field>
                                <Label hidden>Search Driver Notes</Label>
                                <Input
                                    isCompact
                                    placeholder="Search driver notes"
                                    disabled={isBusy}
                                    value={filterText}
                                    onChange={evt => setFilterText(evt.target.value)}
                                />
                            </Field>
                        </div>
                        <div className={css.driverNotesWrapper}>
                            {filteredDriverList.map(driverNote =>
                                <div className={`${css.driverNoteItem} ${getDriverStyleClass(driverNote.lineType)}`}>
                                    <div className={css.driverNoteHeader}>
                                        <h5 className={css.driverNoteHeaderTitle}>{driverNote.lineName}</h5>
                                        <Tag isPill size={"small"} className={css.tag}>{getDriverLabel(driverNote.lineType)}</Tag>
                                    </div>
                                    <p className={css.driverNoteBody}>{driverNote.note}</p>
                                </div>
                            )}
                        </div>
                    </>
                }
            </Body>
            {userCanEdit &&
                <Footer>
                    <FooterItem>
                        <Button
                            isBasic
                            onClick={onClose}
                        >
                            Cancel
                        </Button>
                    </FooterItem>
                    <FooterItem>
                        <Button
                            isPrimary
                            disabled={isBusy || accountNote?.text === localNote.trim()}
                            onClick={() => {
                                if (!accountNote) {
                                    return;
                                }
                                saveNote({
                                    ...accountNote,
                                    text: localNote,
                                });
                            }}
                        >
                            {saveButtonLabel}
                        </Button>
                    </FooterItem>
                </Footer>
            }
            <Close aria-label="Close modal"/>
        </Modal>
    );
}
